/**
 * Multi-diagram tabs — SPEC.md §13 originally listed this as a v1 stretch
 * goal ("Multi-diagram documents / tabs"), built deliberately additive: it
 * never changes what New/Recent/drag-drop/a loaded hash already do to the
 * *active* buffer (SPEC.md §7's shelve invariant, `history.ts`'s
 * `shouldShelve`/`shelveBuffer`, all untouched) — those keep replacing the
 * active buffer and shelving it to `plotter:history` only when actually
 * edited, exactly as before. A tab only ever exists as a *background* one:
 * opening a new tab (App.tsx's `handleOpenNewTab`) parks the current active
 * buffer here and starts a fresh one; clicking a background tab swaps it
 * into the active buffer the same way restoring from Recent already does
 * (App.tsx's `replaceBuffer`) — this module supplies the list bookkeeping
 * around that, not a second buffer-replacement mechanism.
 *
 * Persisted separately from `plotter:draft` (the active buffer's own
 * autosave, unchanged) as `plotter:tabs` — so a browser tab close/reopen
 * restores the whole strip, not just whichever buffer was focused.
 *
 * Pure list logic here, mirroring `history.ts`'s own split between pure
 * logic (`upsertHistoryRow`) and its thin impure `readStorage`/`writeStorage`
 * wrappers.
 */
import { readStorage, writeStorage } from './storage';

export interface BackgroundTab {
  id: string;
  title: string;
  code: string;
}

export const TABS_STORAGE_KEY = 'plotter:tabs';

// A generous cap, not SPEC.md §7's MAX_HISTORY_ROWS (20) — a *background*
// tab is a live buffer someone is actively juggling, not a history entry;
// keeping this smaller than history's cap means a session that opens many
// tabs still hits a believable, browser-like ceiling rather than only ever
// being bounded by history's own much larger allowance.
export const MAX_BACKGROUND_TABS = 11;

/** Moves an existing tab (by id) to the front rather than appending a twin, capped at MAX_BACKGROUND_TABS — same shape as history.ts's upsertHistoryRow, "most recently backgrounded first" instead of "most recently shelved first". */
export function upsertBackgroundTab(tabs: BackgroundTab[], tab: BackgroundTab): BackgroundTab[] {
  const withoutExisting = tabs.filter((existing) => existing.id !== tab.id);
  return [tab, ...withoutExisting].slice(0, MAX_BACKGROUND_TABS);
}

export function removeBackgroundTab(tabs: BackgroundTab[], id: string): BackgroundTab[] {
  return tabs.filter((tab) => tab.id !== id);
}

function isBackgroundTab(value: unknown): value is BackgroundTab {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.title === 'string' && typeof v.code === 'string';
}

/** Corrupt or foreign `plotter:tabs` content degrades to an empty strip, not a crash — same reasoning as history.ts's readHistory. */
export function readBackgroundTabs(): BackgroundTab[] {
  const raw = readStorage(TABS_STORAGE_KEY);
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isBackgroundTab);
  } catch {
    return [];
  }
}

export function writeBackgroundTabs(tabs: BackgroundTab[]): void {
  writeStorage(TABS_STORAGE_KEY, JSON.stringify(tabs));
}
